import { ActionPermission, Api, LogManager, ModelRow, ModelRowType, PermissionsUtil, StringUtil } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import {
    DataDisplayEvent, DataSourceExecutionEvent, Label, Layout, LookupModelSelectionEvent, Tab, Table, TableRow,
    TableRowCreationEvent, TableRowDisplayEvent, TableRowMode, Textbox
} from "@mcleod/components";
import { CommonDialogs } from "@mcleod/common";
import { AutogenLayoutStopNotesAndReferenceNumbers } from "./autogen/AutogenLayoutStopNotesAndReferenceNumbers";
import { QualifierCodeQuickInfo } from "./QualifierCodeQuickInfo";

const log = LogManager.getLogger("lme/dispatch/StopNotesAndReferenceNumbers");

export class StopNotesAndReferenceNumbers extends AutogenLayoutStopNotesAndReferenceNumbers {
    private _stopRow: ModelRow<any>;

    override onLoad(): void {
        if (this._designer != null) return;
        this.tabsetStopNotesRefNos.selectedIndex = -1;
        this.styleTab(this.tabStopComments);
        this.styleTab(this.tabStopRefNumbers);
    }

    set stopRow(data: ModelRow<any>) {
        if (data != this._stopRow) {
            this._stopRow = data;
            this.syncStop();
        }
    }

    get stopRow(): ModelRow<any> {
        return this._stopRow;
    }

    private syncStop() {
        this.tableStopComments.clearRows();
        this.tableStopReferenceNumbers.clearRows();
        if (this.stopRow != null) {
            this.addTableRowCreationListener(this.tableStopComments)
            this.addTableRowCreationListener(this.tableStopReferenceNumbers)
            this.addTableRows(this.tableStopComments, this.stopRow.get("stop_notes"));
            this.addTableRows(this.tableStopReferenceNumbers, this.stopRow.get("reference_numbers"));
        }
    }

    private addTableRowCreationListener(table: Table) {
        if (table["_stopNotesRowCreateListener"] == null) {
            table["_stopNotesRowCreateListener"] = (event: TableRowCreationEvent) => this.onRowCreate(table, event);
            table.addRowCreateListener(table["_stopNotesRowCreateListener"]);
        }
    }

    removeTableRowCreateListeners() {
        if (this.tableStopComments["_stopNotesRowCreateListener"] != null) {
            this.tableStopComments.removeRowCreateListener(this.tableStopComments["_stopNotesRowCreateListener"]);
            this.tableStopComments["_stopNotesRowCreateListener"] = null;
        }
        if (this.tableStopReferenceNumbers["_stopNotesRowCreateListener"] != null) {
            this.tableStopReferenceNumbers.removeRowCreateListener(this.tableStopReferenceNumbers["_stopNotesRowCreateListener"]);
            this.tableStopReferenceNumbers["_stopNotesRowCreateListener"] = null;
        }
    }

    private onRowCreate(table: Table, event: TableRowCreationEvent) {
        this.updateStopsTabCaption(table)
        if (event.getTableRow().data._appending) 
            event.getTableRow().data.set("stop_id", this.stopRow.get("id"));
        if (table.quickAddRow?.findComponentById("fireImage") != null && table.quickAddRow?.findComponentById("fireImage").visible == true)
            table.quickAddRow.findComponentById("fireImage").visible = false;
    }

    tableStopCommentsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        if (PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_BILLING_COMMENTS) && tableRow.data?.get("comment_type") == "BC") {
            tableRow.allowEdit = false;
            tableRow.allowDelete = false;
        }
    }

    private addTableRows(table: Table, data: any[]) {
        table.dataSource.preventChangeNotifications = data != null && data.length > 0;
        if (data != null) {
            for (const [key, value] of Object.entries(data))
                this.addTableRow(table, value, data.length);
        }
    }

    private addTableRow(table: Table, value: any, rowCount: number = -1) {
        const modelRow = value instanceof ModelRow ? value : new ModelRow(table.dataSource.url, false, value);
        table.addRow(modelRow, { mode: TableRowMode.NONE }, { display: true, addToData: true });
        if (table.rows.length == rowCount) {
            table.dataSource.updateOriginalRowCount();
            table.dataSource.preventChangeNotifications = false;
        }
    }

    tableStopReferenceNumbersOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        if (tableRow?.mode === TableRowMode.QUICK_ADD && tableRow.data?.get("send_to_driver") == null) {
            const textboxSendToDriver = tableRow.findComponentById("textboxSendToDriver") as Textbox;
            textboxSendToDriver.setSelectedItemFromValue("N");
            textboxSendToDriver.updateBoundData(tableRow.data, tableRow.table.dataSource.mode);
        }
        const tbRefQual = tableRow.findComponentById("textboxReferenceQual") as Textbox;
        tbRefQual.addLookupModelSelectionListener(this.tbRefQualOnLookupModelSelection);
        tbRefQual.quickInfoOverrideMethod = async () => {
            if (tbRefQual == null || tbRefQual.getFirstLookupModelData() == null) {
                return null;
            }
            const customerId = this.sourceReferenceNumber?.parentDataSource?.data[0]?.get("customer_id");
            const layout = await Layout.getLoadedLayout("lme/dispatch/QualifierCodeQuickInfo") as QualifierCodeQuickInfo;
            const response = await layout.sourceQualifierCode.search({
                customer_id: customerId,
                standard_code: tbRefQual.getFirstLookupModelData()?.get("standard_code"),
            });
            if (response?.modelRows[0]?.data) {
                const is_edi_customer = response.modelRows[0]?.data?.is_edi_customer;
                layout.showLink = !is_edi_customer;
                layout.textboxStandardCodeSC.visible = false;
                layout.textboxDescriptionSC.visible = false;
                layout.textboxDescription.visible = true;
                layout.textboxStandardCode.visible = true;
            } else {
                const standardCodeResponse = await layout.sourceEdiStandardCode.search({
                    standard_code: tbRefQual.getFirstLookupModelData()?.get("standard_code"),
                    element_input: '128',
                    version_input: '004010',
                    customer_id: customerId
                });
                if (standardCodeResponse?.modelRows[0]?.data) {
                    layout.showLink = false;
                    layout.textboxStandardCodeSC.visible = true;
                    layout.textboxDescriptionSC.visible = true;
                    layout.textboxDescription.visible = false;
                    layout.textboxStandardCode.visible = false;
                }
            }
            return layout;
        }
    }

    private tbRefQualOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.selection != null) {
            const tableRow = TableRow.getContainingTableRow(event.target);
            if (tableRow != null)
                tableRow.data.set("description", event.selection.get("description"));
        }
    }

    updateStopsTabCaption(table: Table) {
        const tab = table.parent as Tab;
        let hotCommentCount = 0;
        if (tab != null && tab["countLabel"] != null) {
            tab["countLabel"].text = table.rows.length + "";
            for (const tableRow of table.rows) {
                if (!tableRow.data?.isNull("comment_type") && "HC" === tableRow.data?.get("comment_type"))
                    hotCommentCount++;
            }
            if (hotCommentCount > 0 && !tab["_heading"].contains(tab["hotCommentCountLabel"])) {
                tab["countLabel"].fillRow = false;
                tab["countLabel"].marginRight = 18;
                tab["hotCommentCountLabel"] = new Label({
                    fontBold: true, text: hotCommentCount.toString(), fillRow: true,
                    color: "primary", rowBreak: false
                });
                tab["hotCommentCountLabel"].imageName = ImageName.fire;
                tab["_heading"].insert(tab["hotCommentCountLabel"], 2);
            }
            else if (hotCommentCount == 0 && tab["_heading"].contains(tab["hotCommentCountLabel"])) {
                tab["_heading"].remove(tab["hotCommentCountLabel"]);
                tab["countLabel"].fillRow = true;
            }
            else if (hotCommentCount > 0 && tab["_heading"].contains(tab["hotCommentCountLabel"]))
                tab["hotCommentCountLabel"].text = hotCommentCount.toString();

        }
    }

    textboxReferenceQualBeforeLookupModelSearch(event) {
        const searchFilter = { text_search: (event.target as Textbox).text };
        const layoutStop = this.owner;
        if (layoutStop.owner != null) {
            const customerId = layoutStop.orderSource?.data[0]?.get("customer_id");
            if (!StringUtil.isEmptyString(customerId)) {
                log.debug("Found customer " + customerId);
                searchFilter["customer_id"] = customerId;
                searchFilter["code_type"] = "Qualifier";
                searchFilter["use_enabled_flag"] = "Y";
            }
        } else if (layoutStop.sourceStop != null) {
            const stopId = layoutStop.sourceStop.data[0].get("id");
            if (!layoutStop.sourceStop.data[0]._appending && !StringUtil.isEmptyString(stopId)) {
                log.debug("Using stop  " + stopId);
                searchFilter["stop_id"] = stopId;
                searchFilter["code_type"] = "Qualifier";
                searchFilter["use_enabled_flag"] = "Y";
            }
        }
        event.filter = searchFilter;
    }

    async textboxReferenceQualOnBlur(event) {
        let qualifier = "";
        let sendToDriver = "N";
        let description = "";
        let id = "";
        let elementId = "";
        let version = "";

        try {
            qualifier = event.target.lookupModelData[0].data.standard_code;
            sendToDriver = (StringUtil.isEmptyString(event.target.lookupModelData[0].data.send_to_driver)) ? "N" : event.target.lookupModelData[0].data.send_to_driver;
            description = event.target.lookupModelData[0].data.description;
            id = event.target.lookupModelData[0].data.id;
            elementId = event.target.lookupModelData[0].data.element_id;
            version = event.target.lookupModelData[0].data.version;
        } catch (reason) { }

        const currRefnoRow = event.target.row as TableRow;
        const modelRow = currRefnoRow.data as ModelRow;

        modelRow.set("send_to_driver", sendToDriver)
        const textboxSendToDriver = currRefnoRow.findComponentById("textboxSendToDriver") as Textbox;
        textboxSendToDriver.setSelectedItemFromValue(sendToDriver);

        if (event.target.lookupModelData && StringUtil.isEmptyString(event.target.lookupModelData[0].data.id)) {
            const customerId = this.sourceReferenceNumber?.parentDataSource?.data[0]?.get("customer_id");

            Api.post("lme/dispatch/edi-partner-code-validation", {
                standard_code: qualifier.toUpperCase(),
                element: elementId ?? "128",
                version: version ?? "004010",
                customer_id: customerId
            }).then(result => {
                const textboxRefCode = currRefnoRow.findComponentById("textboxReferenceQual") as Textbox;

                if (result.data[0]?.code_type === "P" || result.data[0]?.code_type === "S") {
                    const lmModelRow = new ModelRow(
                        textboxRefCode.lookupModel,
                        false,
                        {
                            description: result.data[0]?.description,
                            standard_code: qualifier,
                            send_to_driver: result.data[0]?.send_to_driver ?? sendToDriver,
                            element_id: elementId
                        });

                    lmModelRow.type = ModelRowType.LOOKUP_MODEL_DATA;
                    modelRow.setLookupModelData("reference_qual", lmModelRow);
                    modelRow.set("reference_qual", qualifier.toUpperCase());
                    modelRow.set("description", result.data[0]?.description);
                    modelRow.set("send_to_driver", result.data[0]?.send_to_driver)

                    if (result.data[0]?.is_edi_customer) {
                        modelRow.set("partner_id", result.data[0]?.customer_partner_id);
                        modelRow.set("version", result.data[0]?.customer_version);
                        modelRow.set("element_id", elementId ?? "128");
                    }

                    textboxRefCode.displayData(modelRow, null, 0);
                    const textboxSendToDriver = currRefnoRow.findComponentById("textboxSendToDriver") as Textbox;
                    textboxSendToDriver.setSelectedItemFromValue(result.data[0]?.send_to_driver ?? sendToDriver);
                } else {
                    const version = result.data[0]?.is_edi_customer ? result.data[0]?.customer_version : "004010";
                    CommonDialogs.showError(`This qualifier is invalid. Please select from the list or use a valid ${version} qualifier.`);

                    textboxRefCode.text = null;
                    modelRow.set("reference_qual", null);
                    modelRow.set("description", null);
                    modelRow.setLookupModelData("reference_qual", null);
                    textboxRefCode.displayData(modelRow, null, 0);
                }
            });
        }
    }

    saveChangesFromEdit(source: StopNotesAndReferenceNumbers) {
        this.updateSourceTables(this.tableStopComments, source.tableStopComments)
        this.updateSourceTables(this.tableStopReferenceNumbers, source.tableStopReferenceNumbers)
    }

    saveChangesToRow(modelRow: ModelRow) {
        if (this.tableStopComments.dataSource.hasChanged()) {
            modelRow.set("stop_notes", this.tableStopComments.data);
        }
        if (this.tableStopReferenceNumbers.dataSource.hasChanged()) {
            modelRow.set("reference_numbers", this.tableStopReferenceNumbers.data);
        }
    }

    updateSourceTables(thisTable: Table, sourceTable: Table) {
        const dataSource = thisTable.dataSource;
        const sourceTableMap = new Map(sourceTable.rows.map((row) => [row.data.get("id"), row]));
        if (dataSource.deletedData != null) {
            for (const row of dataSource.deletedData) {
                const rowToDelete = sourceTableMap.get(row.get("id"));
                if (rowToDelete != null)
                    rowToDelete.deleteRow();
            }
        }
        for (const modelRow of thisTable.data) {
            const sourceTableRow = sourceTableMap.get(modelRow.get("id"));
            if (sourceTableRow == null) {
                this.addTableRow(sourceTable, modelRow);
                this.updateStopsTabCaption(sourceTable);
            }
            else
                sourceTableRow.data.setValues(modelRow.data);
        }
        sourceTable.displayData(null, sourceTable.data, 0);
        this.updateStopsTabCaption(sourceTable);
    }

    styleTab(tab: Tab) {
        if (tab.heading != null && tab.headingLabel != null) {
            tab.heading.color = "subtle.light";
            tab.headingLabel.setProps({ color: "default", fontBold: true, fillRow: false })
            if (tab["countLabel"] == null) {
                tab["countLabel"] = new Label({
                    fontBold: true, text: "0", fillRow: true,
                    color: "primary", rowBreak: false, marginLeft: 5
                });
                tab.heading.insert(tab["countLabel"], 1);
            }
        }
    }

    /** This is an event handler for the onDataDisplay event of textboxComments.  */
    textboxCommentsOnDataDisplay(event: DataDisplayEvent) {
        if (event.rowData?.get("comment_type") == "HC") {
            const tableRow = TableRow.getContainingTableRow(event.target as Textbox);
            tableRow.findComponentById("fireImage").visible = true;
        }
    }
    /** This is an event handler for the onChange event of textboxCommentType.  */
    textboxCommentTypeOnChange(event) {
        const component = event.target.row?.cells[1] as Textbox;
        if (event.newValue == "Hot comment" && event.userInitiatedChange == true) {
            event.target.row.findComponentById("fireImage").visible = true;
        }
        else if (event.newValue != "Hot comment" && event.userInitiatedChange == true) {
            event.target.row.findComponentById("fireImage").visible = false;
        }
    }

    /** This is an event handler for the onContentsChanged event of tableStopComments.  */
    tableStopCommentsOnContentsChanged(event) {
        this.updateStopsTabCaption(event.getTable());
    }

    sourceStopNoteAfterExecution(event: DataSourceExecutionEvent) {
        if (this.tabStopComments != null && this.tabStopComments["countLabel"] != null) {
            this.tabStopComments["countLabel"].text = this.sourceStopNote.data.length;
        }
    }

    sourceReferenceNumberAfterExecution(event: DataSourceExecutionEvent) {
        if (this.tabStopRefNumbers != null && this.tabStopRefNumbers["countLabel"] != null) {
            this.tabStopRefNumbers["countLabel"].text = this.sourceReferenceNumber.data.length;
        }
    }
}
